import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { Box, useToast } from "@chakra-ui/react";
import Form from "@rjsf/chakra-ui";
import validator from "@rjsf/validator-ajv8";
import { reverseTransformData, transformData } from "../../../util/helper";
import {
  getPersonalityAliasSuggestions,
  getPersonalityDetailsByAlias,
} from "../../../services/Personality";
import { getLocationAliasSuggestions } from "../../../services/Location";
import TypeaheadWidget from "../../../CommonComponents/TypeaheadWidget";
import { RjsfSubmitButton } from "../../../RJSFCommonSchemas";
import FormLoader from "../../../CommonComponents/FormLoader";
import {
  getInstitutionEstablishmentData,
  updateInstitutionEstablishmentData,
} from "../../../services/Institution";
import {
  showErrorToast,
  showInfoToast,
  showSuccessToast,
} from "../../../util/errorHandler";

const Establishment = () => {
  const { alias } = useParams();
  const [processedData, setProcessedData] = useState({});
  const [originalData, setOriginalData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [quickSave, setQuickSave] = useState(false);
  const toast = useToast();

  const schema = {
    title: "Institution Establishment Fields",
    type: "object",
    properties: {
      founders: {
        title: "Founders",
        type: "array",
        items: {
          type: "object",
          properties: {
            alias: { type: "string" },
            first_name: { type: "string" },
            last_name: { type: "string" },
            full_name: { type: "string" },
            image_alias: { type: "string" },
          },
        },
      },
      current_directors: {
        title: "Current Directors",
        type: "array",
        items: {
          type: "object",
          properties: {
            alias: { type: "string" },
            first_name: { type: "string" },
            last_name: { type: "string" },
            full_name: { type: "string" },
            image_alias: { type: "string" },
          },
        },
      },
      city_of_establishment: {
        title: "City of Establishment",
        type: "array",
        items: {
          type: "object",
          properties: {
            alias: { type: "string" },
            name: { type: "string" },
          },
        },
      },
      country_of_establishment: {
        title: "Country of Establishment",
        type: "array",
        items: {
          type: "object",
          properties: {
            alias: { type: "string" },
            name: { type: "string" },
          },
        },
      },
    },
  };

  const uiSchema = {
    ...RjsfSubmitButton(isSubmitting),
    founders: {
      items: {
        alias: {
          "ui:widget": "personalityAliasTypeahead",
        },
        first_name: {
          "ui:readonly": true,
        },
        last_name: {
          "ui:readonly": true,
        },
        full_name: {
          "ui:readonly": true,
        },
        image_alias: {
          "ui:readonly": true,
        },
      },
    },
    current_directors: {
      items: {
        alias: {
          "ui:widget": "personalityAliasTypeahead",
        },
        first_name: {
          "ui:readonly": true,
        },
        last_name: {
          "ui:readonly": true,
        },
        full_name: {
          "ui:readonly": true,
        },
        image_alias: {
          "ui:readonly": true,
        },
      },
    },
    city_of_establishment: {
      items: {
        alias: {
          "ui:widget": "locationAliasTypeahead",
        },
        name: {
          "ui:readonly": true,
        },
      },
    },
    country_of_establishment: {
      items: {
        alias: {
          "ui:widget": "locationAliasTypeahead",
        },
        name: {
          "ui:readonly": true,
        },
      },
    },
  };

  // State to track form data
  const [formData, setFormData] = useState({});
  const [formKey, setFormKey] = useState(0);
  const formRef = useRef(null);

  // Function to identify changed items for array fields
  const getChangedItems = (currentData, originalData, fieldName) => {
    const currentArray = currentData[fieldName] || [];
    const originalArray = originalData[fieldName] || [];

    const changedItems = [];
    const processedOriginalAliases = new Set();

    // Create a map of original items by alias for efficient lookup
    const originalMap = new Map();
    originalArray.forEach((item) => {
      if (item.alias) {
        originalMap.set(item.alias, item);
      }
    });

    // Check current items against original
    currentArray.forEach((currentItem) => {
      if (currentItem.alias && originalMap.has(currentItem.alias)) {
        // Item exists in original - check if it's been updated
        const originalItem = originalMap.get(currentItem.alias);

        // Compare each field individually
        const hasChanges = Object.keys(currentItem).some((key) => {
          return currentItem[key] !== originalItem[key];
        });

        if (hasChanges) {
          changedItems.push({
            ...currentItem,
            type: "update",
          });
        }

        processedOriginalAliases.add(currentItem.alias);
      } else {
        // No alias or not in original - this is a new item
        changedItems.push({
          ...currentItem,
          type: "add",
        });
      }
    });

    // Check for deleted items (items that exist in original but not in current)
    originalArray.forEach((originalItem) => {
      if (
        originalItem.alias &&
        !processedOriginalAliases.has(originalItem.alias)
      ) {
        changedItems.push({
          ...originalItem,
          type: "delete",
        });
      }
    });

    return changedItems;
  };

  // Update form data when processedData changes
  useEffect(() => {
    if (processedData && Object.keys(processedData).length > 0) {
      setFormData({
        ...processedData,
      });
    }
  }, [processedData]);

  // Handle form changes
  const handleFormChange = (formData) => {
    setFormData({
      ...formData.formData,
    });
  };

  const fetchPersonalityAliasSuggestions = async (inputValue) => {
    const fetchedMasterListAliasSuggestions =
      await loadPersonalityAliasSuggestions(inputValue);

    // Ensure inputValue is string and trimmed
    if (
      !inputValue ||
      typeof inputValue !== "string" ||
      !fetchedMasterListAliasSuggestions
    ) {
      return [];
    }

    return fetchedMasterListAliasSuggestions?.map((item) => ({
      label: item.alias,
      value: item.alias,
    }));
  };

  const loadPersonalityAliasSuggestions = async (inputValue) => {
    try {
      const result = getPersonalityAliasSuggestions(20, 1, inputValue);
      return result?.data?.data || [];
    } catch (error) {
      showErrorToast(toast, "Failed to load document types");
    }
  };

  // City/country suggestions carry `name` alongside `alias` (admin/location/location-suggestions
  // was extended to return it), so unlike the personality typeahead below, no second lookup is
  // needed to auto-fill the row -- the selected option already has everything.
  const fetchLocationAliasSuggestions = async (inputValue) => {
    if (!inputValue || typeof inputValue !== "string") {
      return [];
    }
    try {
      const result = await getLocationAliasSuggestions(20, 1, inputValue);
      const suggestions = result?.data || [];
      return suggestions.map((item) => ({
        label: item.alias,
        value: item.alias,
        name: item.name,
      }));
    } catch (error) {
      showErrorToast(toast, "Failed to load location suggestions");
      return [];
    }
  };

  // Function to handle auto-fill when a Personality is selected (founders / current_directors)
  const handlePersonalitySelection = async (selectedOption, fieldPath) => {
    if (!selectedOption || !selectedOption.value) return;

    try {
      const personalityData = await getPersonalityDetailsByAlias(
        selectedOption.value,
      );
      if (personalityData) {
        // Extract the field path components (e.g., "root_founders_0" -> ["founders", "0"])
        const pathParts = fieldPath.split("_");
        const fieldName = pathParts[1]; // founders or current_directors
        const itemIndex = parseInt(pathParts[2]);

        setFormData((prevData) => {
          const newData = { ...prevData };
          if (!newData[fieldName]) {
            newData[fieldName] = [];
          }
          if (!newData[fieldName][itemIndex]) {
            newData[fieldName][itemIndex] = {};
          }

          newData[fieldName][itemIndex] = {
            ...newData[fieldName][itemIndex],
            first_name: personalityData?.first_name || "",
            last_name: personalityData?.last_name || "",
            full_name: personalityData?.full_name || "",
            alias: selectedOption.value,
            image_alias: personalityData?.image_alias || "",
          };

          return newData;
        });

        setTimeout(() => {
          setFormKey((prev) => prev + 1);
        }, 100);
      }
    } catch (error) {
      console.error("Error fetching personality details:", error);
      showErrorToast(toast, "Failed to fetch personality details");
    }
  };

  // Function to handle auto-fill when a Location is selected (city / country of establishment)
  const handleLocationSelection = (selectedOption, fieldPath) => {
    if (!selectedOption || !selectedOption.value) return;

    // "root_city_of_establishment_0" -> fieldName = "city_of_establishment", itemIndex = 0
    // (field name itself contains underscores, so join every part except the first
    // "root" segment and the last numeric index segment, rather than a fixed index)
    const pathParts = fieldPath.split("_");
    const itemIndex = parseInt(pathParts[pathParts.length - 1]);
    const fieldName = pathParts.slice(1, -1).join("_");

    setFormData((prevData) => {
      const newData = { ...prevData };
      if (!newData[fieldName]) {
        newData[fieldName] = [];
      }
      if (!newData[fieldName][itemIndex]) {
        newData[fieldName][itemIndex] = {};
      }

      newData[fieldName][itemIndex] = {
        ...newData[fieldName][itemIndex],
        alias: selectedOption.value,
        name: selectedOption.name || "",
      };

      return newData;
    });

    setTimeout(() => {
      setFormKey((prev) => prev + 1);
    }, 100);
  };

  const widgets = {
    personalityAliasTypeahead: (props) => {
      const fieldPath = props?.id || "";

      return (
        <TypeaheadWidget
          {...props}
          fetchOptions={fetchPersonalityAliasSuggestions}
          onSelectionChange={(selectedOption) =>
            handlePersonalitySelection(selectedOption, fieldPath)
          }
        />
      );
    },
    locationAliasTypeahead: (props) => {
      const fieldPath = props?.id || "";

      return (
        <TypeaheadWidget
          {...props}
          fetchOptions={fetchLocationAliasSuggestions}
          onSelectionChange={(selectedOption) =>
            handleLocationSelection(selectedOption, fieldPath)
          }
        />
      );
    },
  };

  // Fetch initial data
  useEffect(() => {
    const getEstablishmentData = async () => {
      try {
        if (alias) {
          const result = await getInstitutionEstablishmentData(alias);
          if (result) {
            const fieldsToTransform = [];
            const transformedData = transformData(result, fieldsToTransform);
            setProcessedData(transformedData);
            setOriginalData(transformedData); // Store original data for comparison
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        showErrorToast(toast, "Failed to fetch data");
      } finally {
        setIsLoading(false);
      }
    };

    getEstablishmentData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alias, toast, quickSave]);

  // Submit updated data
  const handleSubmit = async (formData) => {
    try {
      if (alias) {
        setIsSubmitting(true);

        const arrayFields = [
          "founders",
          "current_directors",
          "city_of_establishment",
          "country_of_establishment",
        ];
        const changedData = {};
        const deletedData = {};
        let hasChanges = false;

        arrayFields.forEach((fieldName) => {
          const changedItems = getChangedItems(
            formData,
            originalData,
            fieldName,
          );

          const addsAndUpdates = changedItems.filter(
            (item) => item.type === "add" || item.type === "update",
          );
          const deletes = changedItems.filter((item) => item.type === "delete");

          if (addsAndUpdates.length > 0) {
            changedData[fieldName] = addsAndUpdates;
            hasChanges = true;
          }

          if (deletes.length > 0) {
            deletedData[fieldName] = deletes;
            hasChanges = true;
          }
        });

        if (hasChanges) {
          const fieldsToTransform = [];
          const updatedFormData = reverseTransformData(
            changedData,
            fieldsToTransform,
          );

          const result = await updateInstitutionEstablishmentData(
            alias,
            updatedFormData,
            deletedData,
          );

          if (result?.status === 200) {
            const updatedOriginalData = { ...originalData };
            arrayFields.forEach((fieldName) => {
              const currentArray = formData[fieldName] || [];
              updatedOriginalData[fieldName] = [...currentArray];
            });
            setOriginalData(updatedOriginalData);

            setQuickSave((prev) => !prev);
            setProcessedData(formData);
            showSuccessToast(toast, "Establishment data updated successfully");
          }
        } else {
          showInfoToast(toast, "No changes detected");
        }
      }
    } catch (error) {
      console.error("Error updating establishment data:", error);
      showErrorToast(toast, "Failed to update data");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box maxW="auto">
      {isLoading ? (
        <FormLoader />
      ) : (
        <Form
          ref={formRef}
          key={formKey}
          schema={schema}
          uiSchema={uiSchema}
          formData={formData}
          validator={validator}
          onSubmit={(e) => handleSubmit(e.formData)}
          onChange={handleFormChange}
          widgets={widgets}
        />
      )}
    </Box>
  );
};

export default Establishment;
